import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import {
  UiAnnualStatusCellClick,
  UiAnnualStatusDefinition,
  UiAnnualStatusEntry,
} from './annual-status-calendar.types';

const MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
] as const;

interface AnnualStatusMonth {
  readonly index: number;
  readonly name: string;
  readonly days: number;
}

interface AnnualStatusTooltip {
  readonly entry: UiAnnualStatusEntry;
  readonly status: UiAnnualStatusDefinition;
}

@Component({
  selector: 'ui-annual-status-calendar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './annual-status-calendar.component.html',
  styleUrl: './annual-status-calendar.component.scss',
  host: { class: 'ui-annual-status-calendar-host' },
})
export class UiAnnualStatusCalendarComponent {
  /** Año técnico usado para calcular los días; no se muestra en la interfaz. */
  readonly year = input.required<number>();
  readonly statuses = input<readonly UiAnnualStatusDefinition[]>([]);
  readonly entries = input<readonly UiAnnualStatusEntry[]>([]);
  readonly showQuantity = input(false);
  readonly ariaLabel = input('Estado anual por día');
  /** Habilita el clic en celdas con cantidad mayor a cero. */
  readonly cellClickable = input(false);
  readonly cellClick = output<UiAnnualStatusCellClick>();

  protected readonly dayNumbers = Array.from({ length: 31 }, (_, index) => index + 1);
  protected readonly hoveredCell = signal<{ month: number; day: number } | null>(null);
  protected readonly tooltip = signal<AnnualStatusTooltip | null>(null);
  protected readonly tooltipX = signal(0);
  protected readonly tooltipY = signal(0);

  protected readonly months = computed<readonly AnnualStatusMonth[]>(() =>
    MONTH_NAMES.map((name, index) => ({
      index,
      name,
      days: new Date(this.year(), index + 1, 0).getDate(),
    })),
  );

  private readonly statusMap = computed(
    () => new Map(this.statuses().map(status => [status.id, status])),
  );

  private readonly entryMap = computed(
    () => new Map(this.entries().map(entry => [entry.date, entry])),
  );

  protected readonly monthTotals = computed(() => {
    const totals = Array.from({ length: MONTH_NAMES.length }, () => 0);
    const year = String(this.year());
    for (const entry of this.entries()) {
      if (!entry.date.startsWith(`${year}-`)) {
        continue;
      }
      const monthIndex = Number(entry.date.slice(5, 7)) - 1;
      const day = Number(entry.date.slice(8, 10));
      if (monthIndex < 0 || monthIndex >= totals.length) {
        continue;
      }
      const daysInMonth = new Date(this.year(), monthIndex + 1, 0).getDate();
      if (day < 1 || day > daysInMonth) {
        continue;
      }
      totals[monthIndex] += entry.quantity ?? 0;
    }
    return totals;
  });

  protected statusFor(month: number, day: number): UiAnnualStatusDefinition | null {
    const entry = this.entryFor(month, day);
    return entry ? this.statusMap().get(entry.statusId) ?? null : null;
  }

  protected entryFor(month: number, day: number): UiAnnualStatusEntry | undefined {
    return this.entryMap().get(this.isoDate(month, day));
  }

  protected cellLabel(month: AnnualStatusMonth, day: number): string {
    const status = this.statusFor(month.index, day);
    const quantity = this.entryFor(month.index, day)?.quantity;
    const quantityLabel =
      this.showQuantity() && quantity !== undefined ? `, cantidad: ${quantity}` : '';
    return `${day} de ${month.name} de ${this.year()}: ${status?.label ?? 'Sin estado'}${quantityLabel}`;
  }

  protected isCellClickable(entry: UiAnnualStatusEntry | undefined): boolean {
    return this.cellClickable() && (entry?.quantity ?? 0) > 0;
  }

  protected onCellClick(entry: UiAnnualStatusEntry | undefined, event: Event): void {
    if (!this.isCellClickable(entry) || !entry) return;
    event.stopPropagation();
    this.cellClick.emit({ date: entry.date });
  }

  protected onCellKeydown(entry: UiAnnualStatusEntry | undefined, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onCellClick(entry, event);
  }

  protected showTooltip(month: number, day: number): void {
    this.hoveredCell.set({ month, day });
    const entry = this.entryMap().get(this.isoDate(month, day));
    const status = entry ? this.statusMap().get(entry.statusId) : null;
    this.tooltip.set(entry && status ? { entry, status } : null);
  }

  protected hideTooltip(): void {
    this.hoveredCell.set(null);
    this.tooltip.set(null);
  }

  protected moveTooltip(event: MouseEvent): void {
    if (!this.tooltip()) return;
    const host = event.currentTarget as HTMLElement;
    const rect = host.getBoundingClientRect();
    const tooltipWidth =
      host.querySelector<HTMLElement>('.ui-annual-status-calendar__tooltip')
        ?.offsetWidth ?? 0;
    const cursorX = event.clientX - rect.left;
    const rightX = cursorX + 14;
    const leftX = cursorX - tooltipWidth - 14;
    const maxX = Math.max(0, rect.width - tooltipWidth);
    const nextX =
      tooltipWidth > 0 && rightX + tooltipWidth > rect.width
        ? leftX
        : rightX;
    this.tooltipX.set(Math.min(Math.max(0, nextX), maxX));
    this.tooltipY.set(Math.max(0, event.clientY - rect.top - 54));
  }

  protected tooltipDate(entry: UiAnnualStatusEntry): string {
    const [year, month, day] = entry.date.split('-');
    return `${day}/${month}/${year}`;
  }

  private isoDate(month: number, day: number): string {
    return `${this.year()}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
}
